/**
 * @deprecated legacy file. This file is depricated and will be removed in the future.
 * Please migrate to useApiQuery instead.
 */

import {useState, useEffect, useMemo, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {captureApiError, ApiError} from 'src/utils/errors';
import * as api from 'src/utils/redux-api-v2';
import {camel} from 'src/utils/camel-snake';
import {shallowEqual} from 'src/utils/shallowEqual';
import {requestIdleCallback} from 'src/utils/requestIdleCallback';
import {defaultState, type ResourceState} from './useResource';
import {serializeQuery} from 'src/utils';


export type {ResourceState};

type Query = {
  [key: string]: string;
};

export type ApiUrl =
  | string
  | {
      pathname: string;
      query?: Query;
    }
  | null
  | undefined;
export type ApiUrlArg = ApiUrl | (() => ApiUrl);

export const IGNORED_STATUS_CODES = [401, 404] as const;

export type BaseApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: unknown;
  onFetch?: () => unknown;
  handleError?: (arg1: ApiError) => unknown;
  shouldFetch?: boolean;
  shouldCamel?: boolean;
  cacheTimeout?: number;
  apiPath?: string;
  deps?: unknown[];
  key?: string;
  // all 400 <= status < 500 codes get logged _to sentry_
  // unless they're included here. We don't log 401 or 404s.
  // only update this to maintain a semantic api contract
  // but to avoid spamming sentry.
  ignoredStatusCodes?: number[];
};

type Refetch = (force?: boolean) => void;

// TODO (kyle): possibly allow for passing deps to give
// consumer more fine grained control.
export function useAdvancedApi<Response>(
  url: ApiUrl,
  {
    method = 'GET',
    key = 'key',
    data,
    onFetch,
    handleResponse,
    handleError,
    shouldFetch = true,
    shouldCamel,
    cacheTimeout,
    // @ts-expect-error - TS4104 - The type 'readonly [401, 404]' is 'readonly' and cannot be assigned to the mutable type 'number[]'.
    ignoredStatusCodes = IGNORED_STATUS_CODES,
    deps,
    ...options
  }: BaseApiOptions & {
    handleResponse: (arg1: Response) => unknown;
  },
): Refetch {
  let pathname: string | null | undefined;
  // @ts-expect-error - TS7034 - Variable 'query' implicitly has type 'any' in some locations where its type cannot be determined.
  let query;
  if (url instanceof Object) {
    pathname = url.pathname;
    query = url.query;
  } else if (typeof url === 'string') {
    pathname = url;
  }

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const aborter = useRef();
  const dispatch = useDispatch();

  const doFetch = (force: undefined | boolean) => {
    if (aborter.current) {
      // @ts-expect-error - TS2339 - Property 'abort' does not exist on type '{}'.
      aborter.current.abort();
      aborter.current = null;
    }

    if (pathname && shouldFetch) {
      const definedPathname = pathname;

      // this error handler gets called on initial fetch as well as when a response is cached
      // so that calls that specify a cache can always expect handleError to be called consistently
      // if it is present (as well as consistent api error handling for cached/initiating calls)
      const useApiErrorHandler = (error: any) => {
        if (error instanceof ApiError) {
          const {status} = error.response;
          // We only capture errors that are our fault.
          if (status < 500 && !ignoredStatusCodes.includes(status)) {
            captureApiError(error);
          }
          if (handleError) {
            handleError(error);
          }
        } else if (
          error instanceof DOMException &&
          error.name === 'AbortError'
        ) {
          // we're the ones who did this
        } else {
          throw error;
        }
      };

      if (cacheTimeout !== undefined && !force) {
        const cacheRecord = apiCache.get({
          pathname,
          // @ts-expect-error - TS7005 - Variable 'query' implicitly has an 'any' type.
          query,
          timeout: cacheTimeout,
          key,
        });

        if (cacheRecord) {
          Promise.resolve(cacheRecord.data)
            .then((record) => {
              handleResponse(record as Response);
            })
            .catch(useApiErrorHandler);
          return;
        }
      }

      onFetch && onFetch();

      const abortController = new AbortController();
      aborter.current = abortController;

      const responseParamPromise = dispatch(
        api.baseRequest(pathname, {
          method,
          //@ts-expect-error - TS7005 - Variable 'query' implicitly has an 'any' type.
          query,
          data,
          signal: abortController.signal,
          options,
        }),
      );

      if (cacheTimeout) {
        apiCache.set({
          pathname: definedPathname,
          // @ts-expect-error - TS7005 - Variable 'query' implicitly has an 'any' type.
          query,
          timeout: cacheTimeout,
          data: responseParamPromise,
          key,
        });
      }
      responseParamPromise
        .then((record) => {
          const data = shouldCamel ? camel(record) : record;
          handleResponse(data as Response);
        })
        .catch(useApiErrorHandler);
    }
  };

  // @ts-expect-error - TS2345 - Argument of type '(force: undefined | boolean) => void' is not assignable to parameter of type 'EffectCallback'.
  useEffect(doFetch, deps || [pathname, serializeQuery(query)]);

  return doFetch;
}

export type ApiState<Response> = ResourceState<Response> & {
  refetch: () => void;
};

/**
 * allows you to pull api responses into React components.
 *
 * initially the return value will be `null`, but it should eventually
 * resolve to the response from the api.
 *
 * @param url  an api path
 *             if it is a false-y value, the data will not be fetched.
 *
 * TODO (kyle): we should handle errors
 */

/**
 * @deprecated legacy hook. Now we have useApiQuery which is backed by react-query
 * and is more powerful.
 */
export function useApi<Response>(
  url: ApiUrl,
  optionsArg?: BaseApiOptions | unknown[],
): ApiState<Response> {
  const [state, setState] = useState<ResourceState<Response>>({
    ...defaultState,
    isLoading: Boolean(url),
  } as ResourceState<Response>);

  const options = {
    onFetch: () => {
      setState((state) => ({...state, isLoading: true}));
    },
    handleResponse: (result: Response) => {
      setState({
        result,
        error: null,
        isLoading: false,
      });
    },
    handleError: (error: Error) => {
      setState({
        result: null,
        error,
        isLoading: false,
      });
    },
  } as const;

  const refetch = useAdvancedApi<Response>(
    url,
    Array.isArray(optionsArg)
      ? {
          ...options,
          deps: optionsArg,
        }
      : {
          ...optionsArg,
          ...options,
        },
  );

  return {
    ...state,
    refetch,
  };
}

/**
 * @deprecated legacy hook. Now we have useApiQuery which is backed by react-query
 * and is more powerful.
 */
export function useApiStub<Stub>(stub: Stub): ResourceState<Stub> & {
  refetch: () => void;
} {
  return {
    ...defaultState,
    isLoading: false,
    result: stub,
    refetch: () => {
      // pass
    },
  };
}

/**
 * @deprecated legacy hook. Now we have useApiQuery which is backed by react-query
 * and is more powerful.
 */
export function useLegacyApi<Response>(
  url: ApiUrl,
  options?: BaseApiOptions,
): Response | null | undefined {
  const [response, setResponse] = useState<Response | null | undefined>(null);

  useAdvancedApi(url, {
    ...options,
    handleResponse: (response) => {
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'SetStateAction<Response | null | undefined>'.
      setResponse(response);
    },
  });

  return response;
}
export default useLegacyApi;

/**
 * @deprecated legacy hook. Now we have useApiQuery which is backed by react-query
 * and is more powerful.
 */
export function useLegacyApiWithParams<Response>(
  getUrl: () => ApiUrl,
  params: unknown[],
  options?: BaseApiOptions,
): Response | null | undefined {
  const url = useMemo(getUrl, params);
  return useLegacyApi(url, options);
}

/**
 * @deprecated will be removed. Please use useApi instead.
 */
export function useLoadingApi<Response>(
  getUrl: () => ApiUrl,
  params: unknown[],
  options?: BaseApiOptions,
): {
  response: Response | null | undefined;
  loaded: boolean;
} {
  const response = useLegacyApiWithParams(getUrl, params, options);
  // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Response | null | undefined'.
  return {response, loaded: response != null};
}

type CacheRecord = {
  pathname: string;
  query: Query | null | undefined;
  timeout: number; // milliseconds;
  time: number;
  data: unknown;
  key?: string;
};

class ApiCache {
  _cache: Map<string, CacheRecord> = new Map();

  get({
    pathname,
    query,
    timeout,
    key,
  }: {
    pathname: string;
    query: Query | null | undefined;
    timeout: number;
    key: string;
  }): CacheRecord | null | undefined {
    const record = this._cache.get(pathname + key);
    // console.log({key,recordKey:record?.key});

    if (
      record &&
      // TODO (kyle): how slow is this comparison?
      (!shallowEqual(record.query, query) ||
        record.key !== key ||
        Date.now() - record.time > timeout)
    ) {
      this._cache.delete(pathname);
      return null;
    }

    return record;
  }

  set({
    pathname,
    query,
    timeout,
    data,
    key,
  }: {
    pathname: string;
    query: Query | null | undefined;
    timeout: number;
    data: unknown;
    key: string;
  }) {
    const record = {
      pathname,
      query,
      timeout,
      time: Date.now(),
      data,
      key,
    } as const;
    this._cache.set(pathname + key, record);

    requestIdleCallback(this.cull.bind(this));
  }

  evict(pathname: string) {
    this._cache.delete(pathname);
  }

  cull() {
    const currentTime = Date.now();
    this._cache = new Map(
      this._cache
        .entries()
        .filter(
          ([_key, record]: [any, any]) =>
            currentTime - record.time < record.timeout,
        ),
    );
  }
}

export const apiCache: ApiCache = new ApiCache();
